import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import {
  UPDATE_APPLE_PAY_AVAILABLE,
  UPDATE_GOOGLE_PAY_AVAILABLE,
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  TOGGLE_SHOW_MARGIN_IN_SPOOF_MODE,
  TOGGLE_SHOW_SPOOF_LAUNCHPAD,
  DISMISS_LUX_PLUS_FREE_PREVIEW_BANNER,
  HOTEL_CANCELLATION_PRICE_CHANGE_MODAL_SEEN,
  SET_OPTIMIZELY_EXPERIMENTS,
  SET_OPTIMIZELY_FEATURE_FLAGS,
  SET_LAB_FEATURE_FLAGS,
  SET_LAB_REMOTE_CONFIG_FLAGS,
  SET_LAB_V2_EVALUATION,
  SET_HEADLESS_MODE,
  ADD_HISTORICAL_ACTION,
  CLEAR_HISTORICAL_ACTIONS,
  RESTORE_HISTORICAL_ACTIONS,
  CHECKOUT_SET_GUEST_CHECKOUT_MODE,
  CHECKOUT_RESTORE_CART,
} from 'actions/actionConstants'
import {
  FETCH_MERCHANT_FEE_CONFIG,
  FETCH_PAYTO_SUPPORTED_BANKS,
  FETCH_DEPOSIT_CONFIG,
  FETCH_PAYPAL_SETTINGS,
  FETCH_LAB_FEATURE_FLAG_DECISIONS,
  FETCH_LAB_HEADER_BANNERS,
  FETCH_LAB_MODAL_POPUPS,
  FETCH_LAB_REMOTE_CONFIG,
} from 'actions/apiActionConstants'
import { shouldExcludeLereDynamicPricing } from 'lib/lere/lereDynamicPricingEligibility'
import { LOCATION_CHANGE } from 'lib/routing/appRouting'

export const initialFeaturesState: App.FeaturesState = {
  context: undefined,
  experiments: {},
  flags: {},
  labFlags: {},
  remoteConfigFlags: {},
  labV2: {},
  headerBanners: undefined,
  headerBannersRequestPath: undefined,
  headerBannersLoadedPath: undefined,
  modalPopups: undefined,
  modalPopupsRequestPath: undefined,
  modalPopupsRequestKey: undefined,
  historicalActions: [],
}

export const initialSystemState: App.SystemState = {
  appBannerCookie: false,
  customerPortalURL: '',
  originalCanonicalURL: '',
  hostname: '',
  headlessMode: false,
  canPayWithApplePay: false,
  canPayWithGooglePay: false,
  storeMode: false,
  referrer: '',
  device: {
    osName: '',
    browserName: '',
    deviceType: 'other',
  },
  rawUserAgentString: '',
  isVerifiedPricingBot: false,
  previewMode: false,
  merchantFeeConfigs: [],
  merchantFeeConfigCacheKey: undefined,
  payToSupportedBanks: [],
  depositConfigs: undefined,
  payPalSettings: undefined,
  showMarginInSpoofMode: false,
  showSpoofLaunchpad: true,
  luxPlusFreePreviewBannerDismissed: false,
  hotelCancellationPriceChangeModalSeen: false,
  features: {
    ...initialFeaturesState,
  },
}

const apiCalls = apiReducerSwitch<App.SystemState>({
  [FETCH_MERCHANT_FEE_CONFIG]: {
    request: (_state) => {
      // Keep previous configs during refetch to avoid transient fee=0 UI flicker.
      return {}
    },
    success: (_state, action) => {
      return {
        merchantFeeConfigs: action.data.merchantFeeConfigs,
        merchantFeeConfigCacheKey: action.cacheKey,
      }
    },
  },
  [FETCH_PAYTO_SUPPORTED_BANKS]: {
    request: () => ({
      payToSupportedBanks: [],
    }),
    success: (_state, action) => ({
      payToSupportedBanks: action.data,
    }),
  },
  [FETCH_DEPOSIT_CONFIG]: {
    request: () => ({
      depositConfigs: undefined,
    }),
    success: (_state, action) => ({
      depositConfigs: action.data,
    }),
  },
  [FETCH_PAYPAL_SETTINGS]: {
    request: () => ({
      payPalSettings: undefined,
    }),
    success: (_state, action) => ({
      payPalSettings: action.data,
    }),
  },
  [FETCH_LAB_FEATURE_FLAG_DECISIONS]: {
    request: (state) => ({
      features: {
        ...initialFeaturesState,
        context: state.features.context,
        experiments: state.features.experiments,
        flags: state.features.flags,
        labFlags: state.features.labFlags,
        labV2: state.features.labV2,
        remoteConfigFlags: state.features.remoteConfigFlags,
        headerBanners: state.features.headerBanners,
        headerBannersRequestPath: state.features.headerBannersRequestPath,
        headerBannersLoadedPath: state.features.headerBannersLoadedPath,
        modalPopups: state.features.modalPopups,
        modalPopupsRequestPath: state.features.modalPopupsRequestPath,
        modalPopupsRequestKey: state.features.modalPopupsRequestKey,
        historicalActions: state.features.historicalActions,
      },
    }),
    success: (state, action) => ({
      features: {
        ...state.features,
        labFlags: {
          ...state.features.labFlags,
          ...action.data,
        },
      },
    }),
  },
  [FETCH_LAB_HEADER_BANNERS]: {
    request: (state, action) => ({
      features: {
        ...state.features,
        headerBannersRequestPath: action.requestPath,
      },
    }),
    success: (state, action) => {
      if (state.features.headerBannersRequestPath !== action.requestPath) {
        return undefined
      }
      return {
        features: {
          ...state.features,
          headerBanners: action.data,
          headerBannersRequestPath: action.requestPath,
          headerBannersLoadedPath: action.requestPath,
        },
      }
    },
    failure: (state, action) => {
      if (state.features.headerBannersRequestPath !== action.requestPath) {
        return undefined
      }
      return {
        features: {
          ...state.features,
          headerBanners: undefined,
          headerBannersLoadedPath: action.requestPath,
        },
      }
    },
  },
  [FETCH_LAB_MODAL_POPUPS]: {
    request: (state, action) => ({
      features: {
        ...state.features,
        modalPopups: undefined,
        modalPopupsRequestPath: action.requestPath,
        modalPopupsRequestKey: action.requestKey,
      },
    }),
    success: (state, action) => {
      if (state.features.modalPopupsRequestKey !== action.requestKey) {
        return undefined
      }
      return {
        features: {
          ...state.features,
          modalPopups: action.data,
          modalPopupsRequestPath: action.requestPath,
          modalPopupsRequestKey: action.requestKey,
        },
      }
    },
  },
  [FETCH_LAB_REMOTE_CONFIG]: {
    request: () => ({}),
    success: (state, action) => ({
      features: {
        ...state.features,
        remoteConfigFlags: {
          ...state.features.remoteConfigFlags,
          ...action.data,
        },
      },
    }),
  },
})

export default createAppReducer<App.SystemState>(initialSystemState, {
  [LOCATION_CHANGE]: (state, action) => ({
    lereDynamicPricingExcluded: shouldExcludeLereDynamicPricing({
      utmBp: action.payload.location.query.utm_bp,
      isSessionExcluded: state.lereDynamicPricingExcluded,
    }),
  }),
  [CHECKOUT_SET_GUEST_CHECKOUT_MODE]: (state, action) => ({
    lereDynamicPricingExcluded: shouldExcludeLereDynamicPricing({
      isGuestCheckout: action.enabled,
      isSessionExcluded: state.lereDynamicPricingExcluded,
    }),
  }),
  [CHECKOUT_RESTORE_CART]: (state, action) => ({
    lereDynamicPricingExcluded: shouldExcludeLereDynamicPricing({
      isGuestCheckout: action.cartState.isGuestCheckoutMode,
      isSessionExcluded: state.lereDynamicPricingExcluded,
    }),
  }),
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
  [UPDATE_APPLE_PAY_AVAILABLE]: (_state, action) => ({
    canPayWithApplePay: action.value,
  }),
  [UPDATE_GOOGLE_PAY_AVAILABLE]: (_state, action) => ({
    canPayWithGooglePay: action.value,
  }),
  [TOGGLE_SHOW_MARGIN_IN_SPOOF_MODE]: (state, action) => ({
    ...state,
    showMarginInSpoofMode: action.showMarginInSpoofMode,
  }),
  [TOGGLE_SHOW_SPOOF_LAUNCHPAD]: (state, action) => ({
    ...state,
    showSpoofLaunchpad: action.showSpoofLaunchpad,
  }),
  [DISMISS_LUX_PLUS_FREE_PREVIEW_BANNER]: (state) => ({
    ...state,
    luxPlusFreePreviewBannerDismissed: true,
  }),
  [HOTEL_CANCELLATION_PRICE_CHANGE_MODAL_SEEN]: (state) => ({
    ...state,
    hotelCancellationPriceChangeModalSeen: true,
  }),
  // Feature Flags
  [SET_OPTIMIZELY_EXPERIMENTS]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      experiments: {
        ...state.features.experiments,
        ...Object.fromEntries(
          Object.keys(action.experiments).map(flagKey => [
            flagKey,
            { ...state.features.experiments?.[flagKey], ...action.experiments[flagKey] } as App.OptimizelyExperimentDetails,
          ]),
        ),
      },
    },
  }),
  [SET_OPTIMIZELY_FEATURE_FLAGS]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      flags: {
        ...state.features.flags,
        ...action.flags,
      },
    },
  }),
  [SET_LAB_V2_EVALUATION]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      labV2: {
        configs: action.configs,
        uis: action.uis,
      },
    },
  }),
  [SET_LAB_FEATURE_FLAGS]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      labFlags: {
        ...state.features.labFlags,
        ...action.labFlags,
      },
    },
  }),
  [SET_LAB_REMOTE_CONFIG_FLAGS]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      remoteConfigFlags: {
        ...state.features.remoteConfigFlags,
        ...action.remoteConfigFlags,
      },
    },
  }),
  [SET_HEADLESS_MODE]: (state, action) => ({
    ...state,
    headlessMode: action.headlessMode,
  }),
  // Historical Actions
  [ADD_HISTORICAL_ACTION]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      historicalActions: [...state.features.historicalActions, action.action],
    },
  }),
  [CLEAR_HISTORICAL_ACTIONS]: (state) => ({
    ...state,
    features: {
      ...state.features,
      historicalActions: [],
    },
  }),
  [RESTORE_HISTORICAL_ACTIONS]: (state, action) => ({
    ...state,
    features: {
      ...state.features,
      historicalActions: action.actions,
    },
  }),
})
